import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FiX, FiShoppingBag, FiPackage, FiAward } from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";

const ORDER_STATUS_COLORS = {
  initiated: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  authorized: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  captured: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
  verified:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  refunded: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  voided: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  unpaid: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
};

const CONSIGNMENT_STATUS_COLORS = {
  Initiated: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  Processed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  ExpertEdited:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  Confirmed: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200",
  Shipped: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  Received:
    "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  ExpertReEdited:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  Approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  Rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const Section = ({ icon, title, children }) => (
  <div>
    <div className="flex items-center space-x-2 rtl:space-x-reverse mb-3">
      {icon}
      <h4 className="font-semibold text-gray-900 dark:text-white">{title}</h4>
    </div>
    {children}
  </div>
);

const EmptyRow = ({ text }) => (
  <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center bg-gray-50 dark:bg-gray-700/30 rounded-lg">
    {text}
  </p>
);

const InfoTile = ({ label, value }) => (
  <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
      {value}
    </p>
  </div>
);

const UserProfileModal = ({ open, onClose, userId }) => {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const orderStatusLabels = useMemo(
    () => ({
      initiated: t("dashboard.orders.status.initiated"),
      authorized: t("dashboard.orders.status.authorized"),
      paid: t("dashboard.orders.status.paid"),
      captured: t("dashboard.orders.status.captured"),
      verified: t("dashboard.orders.status.verified"),
      refunded: t("dashboard.orders.status.refunded"),
      voided: t("dashboard.orders.status.voided"),
      failed: t("dashboard.orders.status.failed"),
    }),
    [t]
  );

  const consignmentStatusLabels = useMemo(
    () => ({
      Initiated: t("dashboard.consignments.common.status.initiated"),
      Processed: t("dashboard.consignments.common.status.processed"),
      ExpertEdited: t("dashboard.consignments.common.status.expertEdited"),
      Confirmed: t("dashboard.consignments.common.status.confirmed"),
      Shipped: t("dashboard.consignments.common.status.shipped"),
      Received: t("dashboard.consignments.common.status.received"),
      ExpertReEdited: t(
        "dashboard.consignments.common.status.expertReEdited"
      ),
      Approved: t("dashboard.consignments.common.status.approved"),
      Rejected: t("dashboard.consignments.common.status.rejected"),
    }),
    [t]
  );

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get("users/full-profile", {
        params: { user_id: userId },
      });
      setProfile(response.data.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("dashboard.users.profile.loadError")
      );
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, t, onClose]);

  useEffect(() => {
    if (open && userId) {
      fetchProfile();
    } else if (!open) {
      setProfile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  if (!open) return null;

  const dateLocale = i18n.language === "ar" ? "ar-EG" : "en-US";
  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(dateLocale) : "-";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {t("dashboard.users.profile.title")}
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto space-y-6">
            {loading || !profile ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl font-semibold">
                      {(profile.name || "U").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                      {profile.name || t("dashboard.users.unknownUser")}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {profile.number || t("dashboard.users.noNumber")}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <InfoTile
                    label={t("dashboard.users.profile.fields.id")}
                    value={`#${profile.id}`}
                  />
                  <InfoTile
                    label={t("dashboard.users.profile.fields.type")}
                    value={
                      t(`dashboard.users.profile.types.${profile.type}`, {
                        defaultValue: profile.type,
                      })
                    }
                  />
                  <InfoTile
                    label={t("dashboard.users.profile.fields.memberSince")}
                    value={formatDate(profile.createdAt)}
                  />
                  <InfoTile
                    label={t("dashboard.users.profile.fields.number")}
                    value={profile.number || t("dashboard.users.notAvailable")}
                  />
                </div>

                <Section
                  icon={<FiShoppingBag className="w-4 h-4 text-green-500" />}
                  title={t("dashboard.users.profile.sections.orders", {
                    count: profile.orders.length,
                  })}
                >
                  {profile.orders.length === 0 ? (
                    <EmptyRow text={t("dashboard.users.profile.empty.orders")} />
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                          <tr>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.orderId")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.item")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.total")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.status")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.date")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {profile.orders.map((order) => (
                            <tr key={order.id}>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-white">
                                #{order.id}
                              </td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                                {order.itemDesc}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-white">
                                {(order.total || 0).toLocaleString("en-US")}{" "}
                                {t("dashboard.orders.currency")}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    ORDER_STATUS_COLORS[order.paymentStatus] ||
                                    ORDER_STATUS_COLORS.unpaid
                                  }`}
                                >
                                  {order.paymentStatus
                                    ? orderStatusLabels[order.paymentStatus] ||
                                      order.paymentStatus
                                    : t("dashboard.orders.status.unpaid")}
                                </span>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                {formatDate(order.createdAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Section>

                <Section
                  icon={<FiPackage className="w-4 h-4 text-teal-500" />}
                  title={t("dashboard.users.profile.sections.consignments", {
                    count: profile.consignmentApplications.length,
                  })}
                >
                  {profile.consignmentApplications.length === 0 ? (
                    <EmptyRow
                      text={t("dashboard.users.profile.empty.consignments")}
                    />
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                          <tr>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.orderId")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.category")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.products")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.status")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.date")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {profile.consignmentApplications.map((app) => (
                            <tr key={app.id}>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-white">
                                #{app.id}
                              </td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                                {app.category?.name || "-"}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                                {app.products?.length || 0}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    CONSIGNMENT_STATUS_COLORS[app.status] ||
                                    CONSIGNMENT_STATUS_COLORS.Initiated
                                  }`}
                                >
                                  {consignmentStatusLabels[app.status] ||
                                    app.status}
                                </span>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                {formatDate(app.createdAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Section>

                <Section
                  icon={<FiAward className="w-4 h-4 text-yellow-500" />}
                  title={t("dashboard.users.profile.sections.winnings", {
                    count: profile.winnings.length,
                  })}
                >
                  {profile.winnings.length === 0 ? (
                    <EmptyRow text={t("dashboard.users.profile.empty.winnings")} />
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                          <tr>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.product")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.price")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.status")}
                            </th>
                            <th className="px-4 py-2 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              {t("dashboard.users.profile.table.date")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {profile.winnings.map((win) => (
                            <tr key={win.id}>
                              <td className="px-4 py-2 text-gray-900 dark:text-white">
                                {win.product}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                                {(win.price || 0).toLocaleString("en-US")}{" "}
                                {t("dashboard.orders.currency")}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    win.sold
                                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                      : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                  }`}
                                >
                                  {win.sold
                                    ? t("dashboard.users.profile.table.sold")
                                    : t("dashboard.users.profile.table.unsold")}
                                </span>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                {formatDate(win.createdAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

UserProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default UserProfileModal;
